import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleRight, faTimes, faSearch } from "@fortawesome/free-solid-svg-icons";

const SchemeDropdown = ({ 
  schemeCounts = [], 
  selectedScheme = null,
  onSchemeChange = () => {}, 
  isLoading = false,
  schemeColors = {},
  selectedFinancialYear = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);

  // Group schemes by their parent category
  const groupedSchemes = useMemo(() => {
    const groups = {};
    const standaloneSchemes = [];

    schemeCounts.forEach(scheme => {
      const schemeName = scheme.schemeName || '';
      let assignedToGroup = false;
if (schemeName === "Makhana Board (Beneficiary)" || 
          schemeName === "Makhana Board (Asset)" ||
          schemeName.includes("Makhana Board")) {
        const groupName = "Makhana Board";
        if (!groups[groupName]) {
          groups[groupName] = {
            groupName,
            totalSurvey: 0,
            schemes: []
          };
        }
        groups[groupName].schemes.push(scheme);
        groups[groupName].totalSurvey += scheme.totalSurvey || 0;
        assignedToGroup = true;
      }
      // Check for ATMA group
     else if (schemeName.includes("(ATMA)")) {
        const groupName = "ATMA Demonstration";
        if (!groups[groupName]) {
          groups[groupName] = {
            groupName,
            totalSurvey: 0,
            schemes: []
          };
        }
        groups[groupName].schemes.push(scheme);
        groups[groupName].totalSurvey += scheme.totalSurvey || 0;
        assignedToGroup = true;
      }
      // Check for M & T group - includes MT-CRM and MT-SMAM
      else if (schemeName.includes("(M & T)") || 
         schemeName.startsWith("MT-") ||
         schemeName.includes("MT-CRM") || 
         schemeName.includes("MT-SMAM") ||
         schemeName.includes("MT-FMTTI") ||
         schemeName.includes("MT-ICAR") ||
         schemeName.includes("MT-Individual") ||
         schemeName === "MT-CRM" || 
         schemeName === "MT-SMAM" ||
         schemeName === "MT-FMTTI Demonstration" ||
         schemeName === "MT-ICAR Machine Demonstration" ||
         schemeName === "MT-CRM CHC Data- API" ||
         schemeName === "MT-CRM Machine Demonstration" ||
         schemeName === "MT-Individual Crm Survey" ||
         schemeName === "MT-Individual SMAM Survey"
        ) {
  const groupName = "M & T Demonstration";
  if (!groups[groupName]) {
    groups[groupName] = {
      groupName,
      totalSurvey: 0,
      schemes: []
    };
  }
  groups[groupName].schemes.push(scheme);
  groups[groupName].totalSurvey += scheme.totalSurvey || 0;
  assignedToGroup = true;
}
      // Check for MIDH group
      else if (schemeName.includes("(MIDH)")) {
        const groupName = "MIDH Demonstration";
        if (!groups[groupName]) {
          groups[groupName] = {
            groupName,
            totalSurvey: 0,
            schemes: []
          };
        }
        groups[groupName].schemes.push(scheme);
        groups[groupName].totalSurvey += scheme.totalSurvey || 0;
        assignedToGroup = true;
      }
      // Check for MOVCD group (case insensitive)
      else if (schemeName.match(/\(MOVCD\)/i) || schemeName.match(/\(MoVCD\)/)) {
        const groupName = "MOVCD Demonstration";
        if (!groups[groupName]) {
          groups[groupName] = {
            groupName,
            totalSurvey: 0,
            schemes: []
          };
        }
        groups[groupName].schemes.push(scheme);
        groups[groupName].totalSurvey += scheme.totalSurvey || 0;
        assignedToGroup = true;
      }
      // Check for NFSM group
      else if (schemeName.includes("(NFSM)")) {
        const groupName = "NFSM Demonstration";
        if (!groups[groupName]) {
          groups[groupName] = {
            groupName,
            totalSurvey: 0,
            schemes: []
          };
        }
        groups[groupName].schemes.push(scheme);
        groups[groupName].totalSurvey += scheme.totalSurvey || 0;
        assignedToGroup = true;
      }
      else if (schemeName.includes("(CIH)")) {
        const groupName = "CIH Demonstration";
        if (!groups[groupName]) {
          groups[groupName] = {
            groupName,
            totalSurvey: 0,
            schemes: []
          };
        }
        groups[groupName].schemes.push(scheme);
        groups[groupName].totalSurvey += scheme.totalSurvey || 0;
        assignedToGroup = true;
      }
      // Check for RKVY DPR group
      else if (schemeName.includes("(RKVY DPR)")) {
        const groupName = "RKVY Demonstration";
        if (!groups[groupName]) {
          groups[groupName] = {
            groupName,
            totalSurvey: 0,
            schemes: []
          };
        }
        groups[groupName].schemes.push(scheme);
        groups[groupName].totalSurvey += scheme.totalSurvey || 0;
        assignedToGroup = true;
      }
      // Check for ACABC and Agroforestry group
      else if (schemeName === "ACABC (Agriventure)" || schemeName === "Agroforestry" || schemeName.includes("ACABC")) {
        const groupName = "ACABC Demonstration";
        if (!groups[groupName]) {
          groups[groupName] = {
            groupName,
            totalSurvey: 0,
            schemes: []
          };
        }
        groups[groupName].schemes.push(scheme);
        groups[groupName].totalSurvey += scheme.totalSurvey || 0;
        assignedToGroup = true;
      }

      // Add to standalone if not assigned to any group
      if (!assignedToGroup) {
        standaloneSchemes.push(scheme);
      }
    });

    // Convert groups object to array and sort
    const groupedArray = Object.values(groups);
    groupedArray.sort((a, b) => a.groupName.localeCompare(b.groupName));
    
    // Sort standalone schemes alphabetically
    standaloneSchemes.sort((a, b) => 
      (a.schemeName || '').localeCompare(b.schemeName || '')
    );
    
    // Sort schemes within each group
    groupedArray.forEach(group => {
      group.schemes.sort((a, b) => 
        (a.schemeName || '').localeCompare(b.schemeName || '')
      );
    });
    
    console.log("Groups:", groupedArray.length);
    console.log("Standalone schemes:", standaloneSchemes.length);
    console.log("Total schemes:", groupedArray.reduce((acc, g) => acc + g.schemes.length, 0) + standaloneSchemes.length);
    
    return { groups: groupedArray, standaloneSchemes };
  }, [schemeCounts]);

  // Filter groups and schemes based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return { groups: groupedSchemes.groups, standaloneSchemes: groupedSchemes.standaloneSchemes };
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    const filteredGroups = groupedSchemes.groups.filter(group => {
      const groupMatches = group.groupName.toLowerCase().includes(searchLower);
      const childMatches = group.schemes.some(scheme => 
        scheme.schemeName?.toLowerCase().includes(searchLower)
      );
      return groupMatches || childMatches;
    });
    
    const filteredStandalone = groupedSchemes.standaloneSchemes.filter(scheme =>
      scheme.schemeName?.toLowerCase().includes(searchLower)
    );
    
    return { groups: filteredGroups, standaloneSchemes: filteredStandalone };
  }, [groupedSchemes, searchTerm]);

  // Get all items for flat list view when searching
  const allFilteredItems = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const items = [];
    
    filteredData.groups.forEach(group => {
      const matchingChildren = group.schemes.filter(scheme =>
        scheme.schemeName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      if (matchingChildren.length > 0) {
        items.push(...matchingChildren);
      } else if (group.groupName.toLowerCase().includes(searchTerm.toLowerCase())) {
        if (group.schemes.length > 0) {
          items.push(group.schemes[0]);
        }
      }
    });
    
    items.push(...filteredData.standaloneSchemes);
    
    return items;
  }, [filteredData, searchTerm]);

  // Get top searched scheme
  const topSearchedScheme = useMemo(() => {
    if (!searchTerm.trim()) return null;
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    let match = filteredData.standaloneSchemes.find(scheme => 
      scheme.schemeName?.toLowerCase().startsWith(searchLower)
    );
    
    if (match) return match;
    
    for (const group of filteredData.groups) {
      match = group.schemes.find(scheme => 
        scheme.schemeName?.toLowerCase().startsWith(searchLower)
      );
      if (match) return match;
    }
    
    if (allFilteredItems.length > 0) return allFilteredItems[0];
    if (filteredData.groups.length > 0 && filteredData.groups[0].schemes.length > 0) {
      return filteredData.groups[0].schemes[0];
    }
    
    return null;
  }, [filteredData, allFilteredItems, searchTerm]);

  // Toggle group expansion
  const toggleGroup = useCallback((groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  }, []);

 const handleSchemeSelect = useCallback((scheme) => {
  console.log("🔄 SchemeModal: Scheme selected:", scheme);
  
  let schemeCode, subSchemeCode, schemeName;
  if (scheme.schemeName === "Makhana Board (Beneficiary)" || 
        scheme.schemeName === "Makhana Board (Asset)" ||
        scheme.schemeName?.includes("Makhana Board")) {
      schemeCode = 76; // Main Makhana Board scheme code
      
      // Extract sub-scheme type from the scheme name
      if (scheme.schemeName.includes("Beneficiary")) {
        subSchemeCode = 56;
        schemeName = "Makhana Board (Beneficiary)";
      } else if (scheme.schemeName.includes("Asset")) {
        subSchemeCode = 57;
        schemeName = "Makhana Board (Asset)";
      } else {
        subSchemeCode = null;
        schemeName = scheme.schemeName;
      }
    }
  // Handle ATMA schemes
  if (scheme.schemeName?.includes("(ATMA)")) {
    schemeCode = 20;
    
    // Extract demo type from the scheme name
    if (scheme.schemeName.includes("(ATMA) SD")) {
      subSchemeCode = 1;  // SD - Special Demonstration
      schemeName = "(ATMA) SD";
    } else if (scheme.schemeName.includes("(ATMA) CFLD")) {
      subSchemeCode = 2;  // CFLD - Cluster Frontline Demonstration
      schemeName = "(ATMA) CFLD";
    } else if (scheme.schemeName.includes("(ATMA) FLD")) {
      subSchemeCode = 3;  // FLD - Frontline Demonstration
      schemeName = "(ATMA) FLD";
    } else {
      // Default to CFLD if not specified
      subSchemeCode = 2;
      schemeName = "(ATMA) CFLD";
    }
  }
  // Handle MT-CRM and MT-SMAM schemes
  else if (scheme.schemeName === "MT-CRM" || scheme.schemeName  === "MT-FMTTI Demonstration" || scheme.schemeName === "MT-SMAM" || 
           scheme.schemeName?.includes("MT-CRM") || scheme.schemeName?.includes("MT-SMAM")|| scheme.schemeName?.includes("MT-FMTTI Demonstration")) {
    schemeCode = parseInt(scheme.schemeCode);
    subSchemeCode = null;
    schemeName = scheme.schemeName;
  }
  else if (scheme.schemeName?.includes("(CIH)")) {
    schemeCode = 16;
    
    // Extract demo type from the scheme name
    if (scheme.schemeName.includes("(CIH) CFLD") ) {
      subSchemeCode = 1;  
      schemeName = "(CIH) CFLD";
    } else if (scheme.schemeName.includes("(CIH) FLD")) {
      subSchemeCode = 2;  
      schemeName = "(CIH) FLD";
    
    } else {
      subSchemeCode = null;
      schemeName = scheme.schemeName;
    }
  }
  // Handle ACABC schemes
  else if (scheme.schemeName?.includes("ACABC")) {
    schemeCode = 18;
    if (scheme.schemeName.includes("Agriventure")) {
      subSchemeCode = 1;
      schemeName = "ACABC (Agriventure)";
    } else if (scheme.schemeName.includes("Nodal Training Institute")) {
      subSchemeCode = 2;
      schemeName = "ACABC (Nodal Training Institute)";
    } else {
      subSchemeCode = null;
      schemeName = scheme.schemeName;
    }
  }
  // Handle ACABC schemes
  else if (scheme.schemeName?.includes("CIH")) {
    schemeCode = 18;
    if (scheme.schemeName.includes("Agriventure")) {
      subSchemeCode = 1;
      schemeName = "ACABC (Agriventure)";
    } else if (scheme.schemeName.includes("Nodal Training Institute")) {
      subSchemeCode = 2;
      schemeName = "ACABC (Nodal Training Institute)";
    } else {
      subSchemeCode = null;
      schemeName = scheme.schemeName;
    }
  }
  // Handle MIDH schemes
  else if (scheme.schemeName?.includes("(MIDH)")) {
    schemeCode = parseInt(scheme.schemeCode);
    // Extract sub-scheme from MIDH if needed
    subSchemeCode = null;
    schemeName = scheme.schemeName;
  }
  else if (scheme.schemeName?.includes("(M & T)")) {
    schemeCode = parseInt(scheme.schemeCode);
    
    subSchemeCode = null;
    schemeName = scheme.schemeName;
  }
  // Handle MOVCD schemes
  else if (scheme.schemeName?.match(/\(MOVCD\)/i) || scheme.schemeName?.match(/\(MoVCD\)/)) {
    schemeCode = parseInt(scheme.schemeCode);
    subSchemeCode = null;
    schemeName = scheme.schemeName;
  }
  // Handle NFSM schemes
  else if (scheme.schemeName?.includes("(NFSM)")) {
    schemeCode = parseInt(scheme.schemeCode);
    subSchemeCode = null;
    schemeName = scheme.schemeName;
  }
  // Handle RKVY DPR schemes
  else if (scheme.schemeName?.includes("(RKVY DPR)")) {
    schemeCode = parseInt(scheme.schemeCode);
    subSchemeCode = null;
    schemeName = scheme.schemeName;
  }
  // Default handling for other schemes
  else {
    schemeCode = parseInt(scheme.schemeCode);
    subSchemeCode = null;
    schemeName = scheme.schemeName;
  }
  
  const schemeObject = {
    schemeCode,
    schemeName,
    ...(subSchemeCode && { subSchemeCode })
  };
  
  console.log("🔄 SchemeModal: Created scheme object:", schemeObject);
  
  onSchemeChange(schemeObject);
  setSearchTerm('');
  setIsModalOpen(false);
}, [onSchemeChange]);

  // Quick select top searched scheme
  const handleQuickSelectTop = useCallback(() => {
    if (topSearchedScheme) {
      handleSchemeSelect(topSearchedScheme);
    }
  }, [topSearchedScheme, handleSchemeSelect]);

  // Handle Enter key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (topSearchedScheme) {
        handleSchemeSelect(topSearchedScheme);
      } else if (allFilteredItems.length > 0) {
        handleSchemeSelect(allFilteredItems[0]);
      }
    }
  }, [topSearchedScheme, allFilteredItems, handleSchemeSelect]);

  // Clear selection
  const handleClearSelection = useCallback((e) => {
    e.stopPropagation();
    console.log("🔄 SchemeModal: Clearing selection");
    onSchemeChange(null);
    setSearchTerm('');
  }, [onSchemeChange]);

  // Modal close handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsModalOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (isModalOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isModalOpen]);

  const handleOpenModal = useCallback((e) => {
    e.stopPropagation();
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSearchTerm('');
  }, []);

  const truncateSchemeName = (schemeName) => {
    if (!schemeName) return '';
    if (schemeName.length <= 25) return schemeName;
    return schemeName.substring(0, 25) + '...';
  };

  // Render a single scheme item
  const renderSchemeItem = (scheme, isSelected, showCount = true) => (
    <div 
      className={`modal-scheme-item ${isSelected ? 'selected' : ''}`}
      onClick={() => handleSchemeSelect(scheme)}
      title={`${scheme.schemeName} (${scheme.totalSurvey?.toLocaleString()} surveys)`}
    >
      <span className="scheme-name">{scheme.schemeName}</span>
      {showCount && (
        <span className="survey-count">
          {scheme.totalSurvey?.toLocaleString()}
        </span>
      )}
    </div>
  );

  // Render group with expandable children
  const renderGroup = (group) => {
    const isExpanded = expandedGroups[group.groupName];
    const isGroupSelected = selectedScheme && group.schemes.some(
      s => s.schemeCode === selectedScheme.schemeCode
    );

    return (
      <div key={group.groupName} className="modal-group-container">
        <div 
          className={`modal-group-header ${isGroupSelected ? 'selected' : ''}`}
          onClick={() => toggleGroup(group.groupName)}
        >
          <span className="group-expand-icon">
            <FontAwesomeIcon icon={isExpanded ? faAngleDown : faAngleRight} />
          </span>
          <span className="group-name">{group.groupName}</span>
          <span className="group-total">{group.totalSurvey?.toLocaleString()}</span>
        </div>
        
        {isExpanded && (
          <div className="modal-group-children">
            {group.schemes.map(scheme => 
              renderSchemeItem(
                scheme, 
                selectedScheme?.schemeCode === scheme.schemeCode,
                true
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const totalSchemesCount = useMemo(() => {
    if (searchTerm.trim()) {
      return allFilteredItems.length;
    }
    const groupsCount = filteredData.groups.reduce((acc, group) => acc + group.schemes.length, 0);
    return groupsCount + filteredData.standaloneSchemes.length;
  }, [filteredData, allFilteredItems, searchTerm]);

  return (
    <>
      <div className="scheme-selector">
        <button 
          className="scheme-selector-button" 
          type="button"
          onClick={handleOpenModal}
          disabled={isLoading}
        >
          {isLoading ? (
            <span>Loading schemes...</span>
          ) : selectedScheme ? (
            <>
              <span className="scheme-short-name">
                {truncateSchemeName(selectedScheme.schemeName)}
                {selectedFinancialYear && selectedFinancialYear !== "ALL" && (
                  <small style={{fontSize: '10px', color: '#666', marginLeft: '5px'}}>
                   
                  </small>
                )}
              </span>
              <button 
                className="clear-selection-button"
                onClick={handleClearSelection}
                title="Clear selection"
              >
                ×
              </button>
            </>
          ) : (
            <span className="select-text">Select Scheme</span>
          )}
          <span className={`selector-arrow ${isModalOpen ? 'open' : ''}`}>
            <FontAwesomeIcon icon={faAngleDown} />
          </span>
        </button>
      </div>
      
      {/* Modal Overlay */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-container" ref={modalRef}>
            <div className="modal-header">
              <h2 className="modal-title">Select Scheme</h2>
              <button 
                className="modal-close-button"
                onClick={handleCloseModal}
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-search-container">
              <div className="search-wrapper">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="modal-search-input"
                  placeholder="Search schemes by name... (Press Enter to select top match)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => e.stopPropagation()}
                />
                {searchTerm && (
                  <button 
                    className="clear-search-button"
                    onClick={() => setSearchTerm('')}
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            
            {searchTerm.trim() && topSearchedScheme && (
              <div className="quick-select-section">
                <div className="quick-select-header">Top Match (Press Enter to select)</div>
                <div 
                  className="quick-select-item"
                  onClick={handleQuickSelectTop}
                  title={`Select: ${topSearchedScheme.schemeName}`}
                >
                  <span className="scheme-name">{topSearchedScheme.schemeName}</span>
                  <span className="survey-count">
                    {topSearchedScheme.totalSurvey?.toLocaleString()}
                  </span>
                </div>
              </div>
            )}
            
            <div className="modal-schemes-list">
              {searchTerm.trim() ? (
                allFilteredItems.length > 0 ? (
                  allFilteredItems.map(scheme => 
                    renderSchemeItem(
                      scheme, 
                      selectedScheme?.schemeCode === scheme.schemeCode,
                      true
                    )
                  )
                ) : (
                  <div className="no-schemes">No schemes match your search</div>
                )
              ) : (
                <>
                  {filteredData.groups.map(group => renderGroup(group))}
                  {filteredData.standaloneSchemes.length > 0 && (
                    <>
                    
                      {filteredData.standaloneSchemes.map(scheme => 
                        renderSchemeItem(
                          scheme, 
                          selectedScheme?.schemeCode === scheme.schemeCode,
                          true
                        )
                      )}
                    </>
                  )}
                  {filteredData.groups.length === 0 && filteredData.standaloneSchemes.length === 0 && (
                    <div className="no-schemes">No schemes available</div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <div className="footer-left">
                <span className="schemes-count">
                  {totalSchemesCount} schemes
                </span>
              </div>
              {selectedScheme && (
                <div className="footer-right">
                  Currently selected: <strong>{truncateSchemeName(selectedScheme.schemeName)}</strong>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        .scheme-selector {
          position: relative;
          display: inline-block;
          width: 300px;
        }
        
        .scheme-selector-button {
             margin-right: 11px;
    display: flex;
    align-items: center;
    width: 60%;
    font-weight: 400;
    padding: 6px 15px;
    background-color: #fff;
    color: #333;
    border: 1px solid #ddd;
    border-radius: 5px;
    cursor: pointer;
    text-align: left;
    font-size: 16px;
    justify-content: space-between;
    transition: all 0.2s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        
        .scheme-selector-button:hover {
          border-color: #007bff;
          box-shadow: 0 2px 8px rgba(0,123,255,0.15);
        }
        
        .scheme-selector-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .select-text {
          color: #6c757d;
        }
        
        .clear-selection-button {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: #999;
          padding: 2px 8px;
          margin-left: 8px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .clear-selection-button:hover {
          background: #f8f9fa;
          color: #333;
        }
        
        .selector-arrow {
          transition: transform 0.2s;
          margin-left: 8px;
          color: #6c757d;
        }
        
        .selector-arrow.open {
          transform: rotate(180deg);
        }
        
        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.2s ease-out;
        }
        
        .modal-container {
          width: 90%;
          max-width: 600px;
          max-height: 80vh;
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          display: flex;
          flex-direction: column;
          animation: slideUp 0.3s ease-out;
          overflow: hidden;
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 24px;
          border-bottom: 1px solid #e9ecef;
          background: #20712f;
        }
        
        .modal-title {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #fff;
        }
        
        .modal-close-button {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #ffffff;
          padding: 8px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        
        .modal-close-button:hover {
          background: #e9ecef;
          color: #333;
        }
        
        .modal-search-container {
          padding: 10px 24px;
          border-bottom: 1px solid #e9ecef;
        }
        
        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .search-icon {
          position: absolute;
          left: 12px;
          color: #6c757d;
          font-size: 14px;
        }
        
        .modal-search-input {
          width: 100%;
          padding: 12px 16px 12px 40px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 16px;
          outline: none;
          transition: all 0.2s;
        }
        
        .modal-search-input:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.1);
        }
        
        .clear-search-button {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #6c757d;
          padding: 4px 8px;
          border-radius: 4px;
        }
        
        .clear-search-button:hover {
          background: #f8f9fa;
          color: #333;
        }
        
        .quick-select-section {
          padding: 16px 24px;
          background: #f0f8ff;
          border-bottom: 2px solid #007bff;
        }
        
        .quick-select-header {
          font-size: 12px;
          font-weight: 600;
          color: #007bff;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .quick-select-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          cursor: pointer;
          border-radius: 8px;
          background: white;
          border: 2px solid #b3d9ff;
          transition: all 0.2s;
        }
        
        .quick-select-item:hover {
          background: #e6f3ff;
          border-color: #007bff;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
        }
        
        .modal-schemes-list {
          overflow-y: auto;
          flex: 1;
          padding: 16px 24px;
          max-height: 400px;
        }
        
        /* Group Styles */
        .modal-group-container {
          margin-bottom: 8px;
        }
        
        .modal-group-header {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          cursor: pointer;
          border-radius: 8px;
          background: #f8f9fa;
          transition: all 0.2s;
          border: 1px solid #e9ecef;
          margin-bottom: 4px;
        }
        
        .modal-group-header:hover {
          background-color: #e9ecef;
          transform: translateX(2px);
        }
        
        .modal-group-header.selected {
          background-color: #e3f2fd;
          border-color: #2196f3;
        }
        
        .group-expand-icon {
          width: 20px;
          margin-right: 12px;
          color: #6c757d;
          font-size: 14px;
        }
        
        .group-name {
          flex: 1;
          font-weight: 600;
          font-size: 14px;
          color: #20712f;
        }
        
        .group-total {
          color: #6c757d;
          font-size: 12px;
          background: white;
          padding: 4px 10px;
          border-radius: 20px;
          min-width: 50px;
          text-align: center;
          font-weight: 500;
        }
        
        .modal-group-children {
          margin-left: 32px;
          border-left: 2px solid #e9ecef;
          padding-left: 12px;
          margin-bottom: 8px;
        }
        
        .standalone-header {
          font-size: 12px;
          font-weight: 600;
          color: #6c757d;
          margin: 16px 0 8px 0;
          padding-bottom: 4px;
          border-bottom: 1px solid #e9ecef;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .modal-scheme-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          cursor: pointer;
          border-radius: 8px;
          margin-bottom: 4px;
          transition: all 0.2s;
          border: 1px solid transparent;
        }
        
        .modal-scheme-item:hover {
          background-color: #f8f9fa;
          border-color: #e9ecef;
          transform: translateX(4px);
        }
        
        .modal-scheme-item.selected {
          background-color: #e3f2fd;
          border-color: #2196f3;
        }
        
        .scheme-name {
          flex: 1;
          font-weight: 600;
          font-size: 14px;
          color: #20712f;

        }
        
        .survey-count {
          color: #6c757d;
          font-size: 12px;
          flex-shrink: 0;
          background: #f8f9fa;
          padding: 4px 10px;
          border-radius: 20px;
          min-width: 50px;
          text-align: center;
          font-weight: 500;
        }
        
        .no-schemes {
          padding: 40px 20px;
          color: #6c757d;
          text-align: center;
          font-style: italic;
          background: #f8f9fa;
          border-radius: 8px;
          font-size: 15px;
        }
        
        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #e9ecef;
          background: #f8f9fa;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .footer-left {
          color: #6c757d;
          font-size: 13px;
        }
        
        .schemes-count {
          font-weight: 600;
          color: #333;
        }
        
        .footer-right {
          font-size: 13px;
          color: #1565c0;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .modal-container {
            width: 95%;
            max-height: 90vh;
          }
          
          .modal-header {
            padding: 16px 20px;
          }
          
          .modal-search-container,
          .quick-select-section,
          .modal-schemes-list,
          .modal-footer {
            padding: 12px 16px;
          }
        }
      `}</style>
    </>
  );
};

SchemeDropdown.propTypes = {
  schemeCounts: PropTypes.arrayOf(
    PropTypes.shape({
      schemeCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      schemeName: PropTypes.string.isRequired,
      totalSurvey: PropTypes.number
    })
  ),
  selectedScheme: PropTypes.object,
  onSchemeChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  schemeColors: PropTypes.object,
  selectedFinancialYear: PropTypes.string
};

SchemeDropdown.defaultProps = {
  schemeCounts: [],
  selectedScheme: null,
  isLoading: false,
  schemeColors: {},
  selectedFinancialYear: ""
};

export default SchemeDropdown;